/**
 * ProductCard — single product tile used on the Products step.
 *
 * Renders: product name + code + type pill + applicable features (NetTeller,
 * Debit Card, POD, etc).  Selected state flips the green "in-cart" treatment
 * per flow-account-opening.html View 2.
 *
 * Bill 2026-05-12 (Screen 25 SPEC line 2403) — when selected (in-cart), a
 * hover-revealed × remove button appears top-right and triggers an
 * `onToggle(code, false)` immediate-remove (audit-logged via the parent's
 * cart.product.removed emit). The × is rendered as a sibling overlay so the
 * activator and the remove button are not nested controls.
 */
import type { MouseEvent } from 'react';
import { Pill } from '@/components/primitives';
import { productCategoryIconUrl } from '@/icons/flat-library';
import type { ProductDefinition } from '@/contexts/ConfigContext';

/**
 * Per-product ownership snapshot rendered as chips on the selected card
 * (Bill 2026-05-12, SPEC Screen 25 line 2416). Display strings are
 * pre-formatted by the parent so this component stays presentation-only:
 *
 *   primaryName       — full display name of the primary owner.
 *   jointNames        — display names of joint owners (rendered as one chip
 *                       with up to 2 names, or a count fallback for 3+).
 *   beneficiaryCount  — number of POD beneficiaries assigned to this product.
 */
export interface ProductOwnership {
  primaryName?: string;
  jointNames: ReadonlyArray<string>;
  beneficiaryCount: number;
  /**
   * BACKLOG-1667 (Bill 2026-10-01: "Product tile is not displaying the POD
   * (details)") — one pre-formatted label per beneficiary on this line, e.g.
   * "Ellen Test · Primary · 100% · Child". When present the count chip gives
   * way to this list.
   */
  beneficiaries?: ReadonlyArray<string>;
}

export interface ProductCardProps {
  product: ProductDefinition;
  featureCodes: ReadonlyArray<string>;
  selected: boolean;
  disabled?: boolean;
  disabledReason?: string;
  onToggle: (code: string, next: boolean) => void;
  /**
   * Optional ownership snapshot — only consumed when `selected=true`.
   * Renders 3 chips (Primary / Joint / Beneficiaries) per SPEC Screen 25.
   */
  ownership?: ProductOwnership;
  /**
   * Screen 25 "cart-item" mode (Bill 2026-06-01 restoration). When true the
   * card represents an in-cart product: clicking the card RE-OPENS the config
   * modal to EDIT (onToggle(code, true)) instead of toggling it off, the
   * hover-× removes, and the optional summary-chip row renders. When false
   * (picker grid) clicking toggles add/remove as before.
   */
  cartItem?: boolean;
  /**
   * Pre-formatted summary chips (Deposit / Services count / Card design …) shown
   * in cart-item mode per SPEC Screen 25 line 2418 (ProductSummaryChipConfig).
   */
  summaryChips?: ReadonlyArray<string>;
}

function acctTypeLabel(acctType: string): string {
  switch (acctType) {
    case '10': return 'DDA';
    case '20': return 'SAV';
    case '30': return 'CD';
    case '40': return 'LOAN';
    case '50': return 'SDB';
    case '60': return 'IRA';
    case '91': return 'ATM';
    default:   return acctType;
  }
}

/**
 * AcctType (Core Director Field 23 numeric) → product-category slug used to
 * look up the flat-library SVG icon.
 *
 * Bill 2026-05-13 — replaces the prior emoji approach with PCB-branded
 * multicolor illustrated icons from `apps/ROP-teams/public/icons/`. Falls
 * back to undefined for non-mapped acctTypes (LOAN, ATM, unknown) — those
 * cards render without an icon, label-only.
 */
function acctTypeSlug(acctType: string): string | undefined {
  switch (acctType) {
    case '10': return 'dda';
    case '20': return 'sav';
    case '30': return 'cd';
    case '50': return 'sdb';
    case '60': return 'ira';
    default:   return undefined;
  }
}

export function ProductCard({
  product,
  featureCodes,
  selected,
  disabled = false,
  disabledReason,
  onToggle,
  ownership,
  cartItem = false,
  summaryChips,
}: ProductCardProps): JSX.Element {
  const showOwnershipChips = selected && ownership !== undefined && (
    ownership.primaryName !== undefined
    || ownership.jointNames.length > 0
    || ownership.beneficiaryCount > 0
  );
  const jointChipLabel = ownership && ownership.jointNames.length > 0
    ? (ownership.jointNames.length <= 2
        ? `Joint: ${ownership.jointNames.join(', ')}`
        : `${ownership.jointNames.length} joint owners`)
    : undefined;
  const beneficiaryDetail = ownership?.beneficiaries ?? [];
  const beneficiaryChipLabel = ownership && ownership.beneficiaryCount > 0 && beneficiaryDetail.length === 0
    ? (ownership.beneficiaryCount === 1
        ? '1 beneficiary'
        : `${ownership.beneficiaryCount} beneficiaries`)
    : undefined;
  const wrapCls = [
    'rao-product-card-wrap',
    selected ? 'is-selected' : '',
    disabled ? 'is-disabled' : '',
  ].filter(Boolean).join(' ');

  const cardCls = [
    'rao-product-card',
    selected ? 'is-selected' : '',
    disabled ? 'is-disabled' : '',
  ].filter(Boolean).join(' ');

  const onRemoveClick = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    if (disabled) return;
    onToggle(product.code, false);
  };

  return (
    <div className={wrapCls}>
      <button
        type="button"
        className={cardCls}
        aria-pressed={selected ? 'true' : 'false'}
        disabled={disabled}
        title={disabled ? disabledReason : (cartItem ? 'Click to edit owners, deposit, and services' : undefined)}
        onClick={() => onToggle(product.code, cartItem ? true : !selected)}
      >
        <div className="rao-product-card__head">
          <div>
            <h4 className="rao-product-card__title">{product.name}</h4>
            <div className="rao-product-card__code">{product.code}</div>
          </div>
          <Pill variant="info">
            {(() => {
              const slug = acctTypeSlug(product.acctType);
              const iconUrl = slug ? productCategoryIconUrl(slug) : undefined;
              return iconUrl ? (
                <img
                  src={iconUrl}
                  alt=""
                  aria-hidden
                  width={16}
                  height={16}
                  className="rao-product-card__icon"
                />
              ) : null;
            })()}
            {acctTypeLabel(product.acctType)}
          </Pill>
        </div>

        {featureCodes.length > 0 && (
          <div className="rao-product-card__features">
            {featureCodes.map((fc) => (
              <Pill key={fc} variant="muted">{fc}</Pill>
            ))}
          </div>
        )}

        {showOwnershipChips && (
          <div className="rao-product-card__ownership">
            {ownership?.primaryName && (
              <Pill variant="ok">Primary: {ownership.primaryName}</Pill>
            )}
            {jointChipLabel && (
              <Pill variant="info">{jointChipLabel}</Pill>
            )}
            {beneficiaryChipLabel && (
              <Pill variant="warn">{beneficiaryChipLabel}</Pill>
            )}
          </div>
        )}

        {/* BACKLOG-1667 — POD designation detail (name · tier · share · relationship). */}
        {selected && beneficiaryDetail.length > 0 && (
          <div className="rao-product-card__pod">
            <span className="rao-product-card__pod-k">POD</span>
            {beneficiaryDetail.map((b) => (
              <Pill key={b} variant="warn">{b}</Pill>
            ))}
          </div>
        )}
        {/* Screen 25 summary chip row (cart-item only): Deposit / Services / Card design. */}
        {cartItem && summaryChips && summaryChips.length > 0 && (
          <div className="rao-product-card__summary">
            {summaryChips.map((c) => (
              <Pill key={c} variant="muted">{c}</Pill>
            ))}
          </div>
        )}

        {selected && !cartItem && <Pill variant="ok">Added ✓</Pill>}
      </button>

      {selected && !disabled && (
        <button
          type="button"
          className="rao-product-card__remove"
          onClick={onRemoveClick}
          aria-label={`Remove ${product.name} from cart`}
          title="Remove from cart"
        >
          ×
        </button>
      )}
    </div>
  );
}
